import type { StyledComponent } from '@emotion/styled';
import styled from '@emotion/styled';

import {
  device,
  Divider,
  fit,
  Icon,
  typography,
  shouldForwardProp,
} from '@landsbankinn/ui';
import React, { forwardRef, useState } from 'react';
import type { FC } from 'react';
import { useSpring, animated } from 'react-spring';

export const CardHostLink: StyledComponent<any> = styled(animated.a, {
  shouldForwardProp: shouldForwardProp('a'),
})<Partial<RelatedCardProps>>`
  display: block;
  position: relative;
  width: 100%;
  color: #fff;
  background-color: ${({ theme, colorTheme }) =>
    (theme.colors as any)[colorTheme]};

  margin-top: ${({ hasIcon }) => (hasIcon ? '90px' : '0')};

  -webkit-user-select: none;
  -webkit-user-drag: none;
  -webkit-app-region: no-drag;
  cursor: pointer;
  user-select: text;
`;

const CardHost = styled(animated.div, {
  shouldForwardProp: shouldForwardProp('div'),
})<Partial<RelatedCardProps>>`
  position: relative;
  width: 100%;
  background-color: ${({ theme, colorTheme }) =>
    (theme.colors as any)[colorTheme]};
`;

const Bg = styled(animated.div)`
  ${fit}
  background-color: ${(props) => props.theme.colors.primary};
`;

const Body = styled.div`
  position: relative;
  z-index: 1;
  padding: 48px 30px 45px;

  @media ${device.tablet} {
    padding: 15%;
  }

  @media ${device.pageLimit} {
    padding: 48px 56px 45px;
  }
`;

const IconWrap = styled.div`
  position: absolute;
  top: -27%;
  right: 30px;

  width: 28%;
`;

const Title = styled.h2`
  line-height: calc(24 / 18);
  font-weight: bold;
`;

const CustomDivider = styled(Divider)`
  margin: 31px 0 25px;
  background-color: currentColor;

  @media ${device.tablet} {
    margin: 10% 0;
  }

  @media ${device.pageLimit} {
    margin: 31px 0 25px;
  }
`;

const Copy = styled.p`
  ${typography.h3}
`;

const ActionSpace = styled.div`
  position: relative;
  opacity: 0;
  pointer-events: none;
`;

const ActionPosition = styled.div`
  display: flex;
  flex-direction: row;
  position: absolute;
  bottom: 45px;
  left: 35px;
  right: 42px;
`;

export interface RelatedCardProps {
  title: React.ReactNode;
  description: React.ReactNode;
  action?: React.ReactNode;
  theme?: 'primary' | 'secondary' | 'ternary';
  ref?: any;
  notLink?: boolean;
  hasIcon?: boolean;
  [key: string]: any;
}

export const CardAction = ({ children }: { children: React.ReactNode }) => {
  return (
    <>
      <ActionSpace>{children}</ActionSpace>
      <ActionPosition>{children}</ActionPosition>
    </>
  );
};

export const RelatedCard: FC<RelatedCardProps> = forwardRef<
  HTMLAnchorElement,
  RelatedCardProps
>(function CardFn(props: RelatedCardProps, ref) {
  const { title, description, action, theme, icon, notLink, ...rest } = props;

  const [isHover, setHover] = useState(false);

  const onHover = useSpring({
    transform: isHover ? 'scale(1.025)' : 'scale(1)',
    config: { mass: 10, tension: 2000, friction: 200 },
  });

  const onClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (window?.getSelection?.()?.type === 'Range') {
      e.preventDefault();
      return false;
    }
  };

  const content = (
    <>
      <Body>
        {icon !== 'Null' && (
          <IconWrap>
            <Icon name="house" />
          </IconWrap>
        )}
        {!!title && <Title>{title}</Title>}
        {!!title && <CustomDivider />}
        <Copy>{description}</Copy>
        {!!action && <CardAction>{action}</CardAction>}
      </Body>
    </>
  );

  if (notLink) {
    return (
      <CardHost colorTheme={theme} hasIcon={icon !== 'Null'} {...rest}>
        {content}
      </CardHost>
    );
  }

  return (
    <CardHostLink
      colorTheme={theme}
      hasIcon={icon !== 'Null'}
      ref={ref}
      {...rest}
      onMouseOver={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onClick}
    >
      <Bg style={{ ...onHover }} />
      {content}
    </CardHostLink>
  );
});
